// ============================================================
// FONADHOO CRM v3.0 - BALLOT COUNT
// Counting-night box-by-box results entry + live running tally.
// Maldives counts each ballot box separately; agents enter each
// box as it's counted and the constituency total updates live.
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from './shared/Icon';
import { hasEffectivePermission } from './shared/constants';

const PALETTE = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

export default function BallotCount({ supabase, user, allVoters = [] }) {
  const [round, setRound] = useState(1);
  const [contestants, setContestants] = useState([]);      // [{name, mine}]
  const [byBox, setByBox] = useState({});                  // box -> {tallies, invalid, counted}
  const [dirty, setDirty] = useState(() => new Set());     // boxes with unsaved edits
  const [savingBox, setSavingBox] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);    // the box being entered (one at a time)
  const [editContestants, setEditContestants] = useState(false);
  const [loading, setLoading] = useState(true);
  const canEditConfig = hasEffectivePermission(user, 'canManageUsers');

  const boxes = useMemo(
    () => [...new Set((allVoters || []).map(v => round === 2 ? v.ballot_box_r2 : v.ballot_box_r1).filter(Boolean))].sort(),
    [allVoters, round]
  );

  // Eligible voters per box (denominator for box turnout-ish context)
  const eligibleByBox = useMemo(() => {
    const m = {};
    for (const v of allVoters || []) {
      const b = round === 2 ? v.ballot_box_r2 : v.ballot_box_r1;
      if (b) m[b] = (m[b] || 0) + 1;
    }
    return m;
  }, [allVoters, round]);

  const loadConfig = async () => {
    const { data } = await supabase.from('app_settings').select('value').eq('key', 'ballot_contestants').maybeSingle();
    const list = data?.value?.list;
    if (Array.isArray(list) && list.length) setContestants(list);
    else setContestants([{ name: 'Our Candidate', mine: true }]);
  };
  const loadResults = async () => {
    const { data } = await supabase.from('ballot_results').select('box, tallies, invalid, counted').eq('round', round);
    const m = {};
    for (const r of data || []) m[r.box] = { tallies: r.tallies || {}, invalid: r.invalid || 0, counted: !!r.counted };
    setByBox(m);
    setDirty(new Set());
    setLoading(false);
  };

  useEffect(() => { loadConfig(); }, []); // eslint-disable-line
  useEffect(() => { setLoading(true); loadResults(); }, [round]); // eslint-disable-line

  // Live sync across agents
  useEffect(() => {
    const ch = supabase.channel('ballot-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ballot_results' }, payload => {
        const r = payload.new; if (!r || r.round !== round) return;
        setByBox(prev => dirty.has(r.box) ? prev : ({ ...prev, [r.box]: { tallies: r.tallies || {}, invalid: r.invalid || 0, counted: !!r.counted } }));
      }).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [supabase, round, dirty]);

  const cell = (box) => byBox[box] || { tallies: {}, invalid: 0, counted: false };
  const markDirty = (box) => setDirty(prev => new Set(prev).add(box));
  const setVote = (box, name, val) => {
    const n = Math.max(0, parseInt(val) || 0);
    setByBox(prev => ({ ...prev, [box]: { ...cell(box), tallies: { ...cell(box).tallies, [name]: n } } }));
    markDirty(box);
  };
  const setInvalid = (box, val) => {
    const n = Math.max(0, parseInt(val) || 0);
    setByBox(prev => ({ ...prev, [box]: { ...cell(box), invalid: n } }));
    markDirty(box);
  };

  // Big tap-target +/- stepper (easy vote entry on mobile).
  const stepBtn = { width: 42, height: 44, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 22, fontWeight: 700, cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0, userSelect: 'none', lineHeight: 1, padding: 0 };
  const saveBox = async (box, markCounted) => {
    setSavingBox(box);
    const c = cell(box);
    const counted = markCounted !== undefined ? markCounted : c.counted;
    const row = { box, round, tallies: c.tallies || {}, invalid: c.invalid || 0, counted, updated_by: user?.id || null, updated_at: new Date().toISOString() };
    const { error } = await supabase.from('ballot_results').upsert(row, { onConflict: 'box,round' });
    setSavingBox(null);
    if (error) { alert('Save failed: ' + error.message); return; }
    setByBox(prev => ({ ...prev, [box]: { tallies: c.tallies || {}, invalid: c.invalid || 0, counted } }));
    setDirty(prev => { const n = new Set(prev); n.delete(box); return n; });
  };

  // Default the focused box to the first uncounted one; keep valid on round change.
  useEffect(() => {
    setSelectedBox(prev => (prev && boxes.includes(prev)) ? prev : (boxes.find(b => !byBox[b]?.counted) || boxes[0] || null));
  }, [boxes]); // eslint-disable-line
  const saveAndNext = async (box) => {
    await saveBox(box, true);
    const next = boxes.find(b => b !== box && !cell(b).counted);
    if (next) setSelectedBox(next);
  };

  const saveContestants = async (list) => {
    setContestants(list);
    await supabase.from('app_settings').upsert({ key: 'ballot_contestants', value: { list } });
  };

  // ── Aggregate over counted boxes ────────────────────────────
  const agg = useMemo(() => {
    const totals = {}; contestants.forEach(c => totals[c.name] = 0);
    let invalid = 0, countedBoxes = 0, totalVotes = 0;
    for (const box of boxes) {
      const c = byBox[box];
      if (!c || !c.counted) continue;
      countedBoxes++;
      invalid += c.invalid || 0;
      for (const ct of contestants) { const v = Number(c.tallies?.[ct.name]) || 0; totals[ct.name] += v; totalVotes += v; }
    }
    const ranked = contestants.map(c => ({ ...c, votes: totals[c.name] })).sort((a, b) => b.votes - a.votes);
    const leader = ranked[0];
    const mine = ranked.find(r => r.mine);
    const others = ranked.filter(r => !r.mine);
    const topOther = others.length ? Math.max(...others.map(o => o.votes)) : 0;
    const margin = mine ? mine.votes - topOther : 0;
    return { ranked, leader, mine, margin, invalid, countedBoxes, totalVotes };
  }, [byBox, boxes, contestants]);

  const boxLeader = (box) => {
    const c = byBox[box]; if (!c) return null;
    let best = null;
    for (const ct of contestants) { const v = Number(c.tallies?.[ct.name]) || 0; if (best === null || v > best.v) best = { name: ct.name, v, mine: ct.mine }; }
    return best && best.v > 0 ? best : null;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-2)' }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--border-2)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        Loading count…
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const colorFor = (name) => { const i = contestants.findIndex(c => c.name === name); return PALETTE[i % PALETTE.length]; };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#8b5cf61f', color: '#8b5cf6' }}>
            <Icon name="vote" size={20} />
          </span>
          <div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Counting Night</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>Ballot Count</h2>
          </div>
        </div>
        <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
          {[1, 2].map(r => (
            <button key={r} onClick={() => setRound(r)} style={{ padding: '8px 14px', border: 'none', background: round === r ? 'rgba(59,130,246,0.18)' : 'transparent', color: round === r ? '#60a5fa' : 'var(--text-2)', fontSize: 12.5, fontWeight: 600, fontFamily: "'DM Mono', monospace", cursor: 'pointer' }}>R{r}</button>
          ))}
        </div>
      </div>

      {/* Live standings */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text)' }}>Live Standings</h3>
          <div style={{ display: 'flex', gap: 14, fontFamily: "'DM Mono', monospace", fontSize: 11.5, color: 'var(--text-3)' }}>
            <span>{agg.countedBoxes}/{boxes.length} boxes</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{agg.totalVotes.toLocaleString()} valid · {agg.invalid.toLocaleString()} invalid</span>
          </div>
        </div>

        {/* Mine headline */}
        {agg.mine && (
          <div style={{ marginBottom: 14, padding: '12px 14px', borderRadius: 10, background: agg.margin >= 0 ? '#10b98112' : '#ef444412', border: `1px solid ${agg.margin >= 0 ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}` }}>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: agg.margin >= 0 ? '#10b981' : '#ef4444' }}>
              {agg.margin >= 0 ? `Leading by ${agg.margin.toLocaleString()}` : `Trailing by ${Math.abs(agg.margin).toLocaleString()}`}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 10, fontFamily: "'DM Mono', monospace" }}>{agg.countedBoxes < boxes.length ? `${boxes.length - agg.countedBoxes} boxes left` : 'all boxes counted'}</span>
          </div>
        )}

        {/* Ranked bars */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {agg.ranked.map((c, i) => {
            const pct = agg.totalVotes > 0 ? (c.votes / agg.totalVotes) * 100 : 0;
            const col = colorFor(c.name);
            return (
              <div key={c.name}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                  <span style={{ color: 'var(--text)', fontWeight: c.mine ? 700 : 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {i === 0 && c.votes > 0 && <Icon name="trophy" size={13} color="#fbbf24" />}
                    {c.name}{c.mine && <span style={{ fontSize: 9, fontFamily: "'DM Mono', monospace", color: '#60a5fa', border: '1px solid rgba(59,130,246,0.5)', borderRadius: 4, padding: '0 5px' }}>OURS</span>}
                  </span>
                  <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>{c.votes.toLocaleString()} <span style={{ color: 'var(--text-4)' }}>({pct.toFixed(1)}%)</span></span>
                </div>
                <div style={{ height: 10, background: 'var(--surface-2)', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: col, borderRadius: 5, transition: 'width 0.4s ease' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Contestants editor */}
        {canEditConfig && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <button onClick={() => setEditContestants(v => !v)} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 12, fontFamily: "'DM Mono', monospace", display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0 }}>
              <Icon name="settings" size={13} /> Contestants ({contestants.length}) {editContestants ? '▾' : '▸'}
            </button>
            {editContestants && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {contestants.map((c, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button type="button" title="Mark as our candidate" onClick={() => saveContestants(contestants.map((x, j) => ({ ...x, mine: j === i })))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.mine ? '#60a5fa' : 'var(--text-4)', padding: 0, lineHeight: 0 }}>
                      <Icon name="check" size={15} />
                    </button>
                    <input value={c.name} onChange={e => setContestants(contestants.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                      onBlur={() => saveContestants(contestants)}
                      style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 13 }} />
                    <button type="button" onClick={() => saveContestants(contestants.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
                  </div>
                ))}
                <button type="button" onClick={() => saveContestants([...contestants, { name: '', mine: false }])}
                  style={{ alignSelf: 'flex-start', padding: '7px 12px', borderRadius: 8, border: '1px dashed var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="plus" size={12} /> Add contestant
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Per-box entry — one box at a time */}
      {boxes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>No ballot boxes set for round {round}. Set voters' Round {round} ballot box on their profiles first.</div>
      ) : (
        <>
          {/* Progress — counted vs remaining (scales to dozens of boxes) */}
          {(() => {
            const done = boxes.filter(b => byBox[b]?.counted).length;
            return (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, fontFamily: "'DM Mono', monospace", color: 'var(--text-3)', marginBottom: 6 }}>
                  <span>{boxes.length} boxes · <span style={{ color: '#10b981' }}>{done} counted</span> · {boxes.length - done} left</span>
                  <span style={{ color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{boxes.length ? Math.round(done / boxes.length * 100) : 0}%</span>
                </div>
                <div style={{ height: 6, background: 'var(--surface-2)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${boxes.length ? done / boxes.length * 100 : 0}%`, background: '#10b981', borderRadius: 4, transition: 'width 0.4s ease' }} />
                </div>
              </div>
            );
          })()}

          {/* Box picker strip — status at a glance, tap to jump. Scroll-capped so
              the entry card stays visible when there are many boxes. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, maxHeight: 172, overflowY: 'auto' }}>
            {boxes.map(box => {
              const counted = byBox[box]?.counted;
              const sel = selectedBox === box;
              return (
                <button key={box} onClick={() => setSelectedBox(box)} style={{
                  padding: '8px 12px', borderRadius: 9, cursor: 'pointer', fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600,
                  border: sel ? '1px solid #3b82f6' : `1px solid ${counted ? 'rgba(16,185,129,0.4)' : 'var(--border)'}`,
                  background: sel ? 'rgba(59,130,246,0.13)' : 'transparent',
                  color: sel ? '#60a5fa' : counted ? '#10b981' : 'var(--text-2)',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                  <Icon name={counted ? 'check' : 'vote'} size={13} />{box}
                </button>
              );
            })}
          </div>

          {/* Focused entry for the selected box */}
          {selectedBox && (() => {
            const box = selectedBox;
            const c = cell(box);
            const isDirty = dirty.has(box);
            const idx = boxes.indexOf(box);
            const lead = boxLeader(box);
            const boxTotal = contestants.reduce((s, ct) => s + (Number(c.tallies?.[ct.name]) || 0), 0) + (c.invalid || 0);
            return (
              <div style={{ background: 'var(--surface)', border: `1px solid ${c.counted ? 'rgba(16,185,129,0.4)' : 'var(--border)'}`, boxShadow: 'var(--shadow-sm)', borderRadius: 14, padding: 20, maxWidth: 520, margin: '0 auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-strong)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Icon name="vote" size={18} color="var(--text-3)" /> {box}
                  </h3>
                  {c.counted && !isDirty && <span style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: '#10b981', border: '1px solid rgba(16,185,129,0.5)', borderRadius: 5, padding: '2px 8px' }}>COUNTED</span>}
                  {isDirty && <span style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: '#f59e0b' }}>unsaved</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace", marginBottom: 16 }}>
                  Box {idx + 1} of {boxes.length} · {eligibleByBox[box] || 0} eligible{lead ? ` · leading: ${lead.name}` : ''}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {contestants.map(ct => (
                    <div key={ct.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ width: 11, height: 11, borderRadius: 3, background: colorFor(ct.name), flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 15, color: ct.mine ? 'var(--text-strong)' : 'var(--text)', fontWeight: ct.mine ? 700 : 500 }}>
                        {ct.name || '—'}{ct.mine && <span style={{ fontSize: 9, fontFamily: "'DM Mono', monospace", color: '#60a5fa', border: '1px solid rgba(59,130,246,0.5)', borderRadius: 4, padding: '0 5px', marginLeft: 6 }}>OURS</span>}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                        <button type="button" aria-label={`Minus one for ${ct.name}`} onClick={() => setVote(box, ct.name, (Number(c.tallies?.[ct.name]) || 0) - 1)} style={stepBtn}>−</button>
                        <input type="number" min="0" inputMode="numeric" value={c.tallies?.[ct.name] ?? ''} onChange={e => setVote(box, ct.name, e.target.value)}
                          style={{ width: 58, padding: '10px 6px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 18, fontWeight: 700, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }} />
                        <button type="button" aria-label={`Plus one for ${ct.name}`} onClick={() => setVote(box, ct.name, (Number(c.tallies?.[ct.name]) || 0) + 1)} style={stepBtn}>+</button>
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                    <span style={{ width: 11, height: 11, borderRadius: 3, background: 'var(--text-4)', flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 14, color: 'var(--text-3)' }}>Invalid / void</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                      <button type="button" aria-label="Minus one invalid" onClick={() => setInvalid(box, (Number(c.invalid) || 0) - 1)} style={stepBtn}>−</button>
                      <input type="number" min="0" inputMode="numeric" value={c.invalid ?? ''} onChange={e => setInvalid(box, e.target.value)}
                        style={{ width: 58, padding: '10px 6px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)', fontSize: 16, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }} />
                      <button type="button" aria-label="Plus one invalid" onClick={() => setInvalid(box, (Number(c.invalid) || 0) + 1)} style={stepBtn}>+</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace", marginTop: 2 }}>
                    <span>Box total</span><span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>{boxTotal.toLocaleString()}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                  <button onClick={() => saveAndNext(box)} disabled={savingBox === box}
                    style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', background: '#10b981', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Icon name="check" size={16} color="#fff" /> {savingBox === box ? 'Saving…' : 'Save & next box'}
                  </button>
                  <button onClick={() => saveBox(box, c.counted)} disabled={savingBox === box}
                    style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                </div>
                {c.counted && (
                  <button onClick={() => saveBox(box, false)} disabled={savingBox === box}
                    style={{ marginTop: 8, width: '100%', padding: '8px', borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-3)', fontSize: 12, cursor: 'pointer' }}>Reopen this box</button>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
